import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { NotificationManager } from "react-notifications";
import { useAppAuth } from "../auth/app-auth";
import { createUser, getUsers, updateUser } from "../services/requests";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    maxWidth: 1100,
    padding: theme.spacing(14, 4, 4),
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
  },
  table: {
    minWidth: 650,
  },
  form: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    minWidth: 560,
  },
  fullWidth: {
    gridColumn: "1 / -1",
  },
  errorText: {
    color: theme.palette.error.main,
    minHeight: theme.spacing(3),
  },
}));

const emptyForm = {
  name: "",
  email: "",
  role: "member",
  status: "active",
  password: "",
};

const formatDate = (value) => {
  if (!value) {
    return "Never";
  }

  return new Date(value).toLocaleString();
};

const Users = () => {
  const styles = useStyles();
  const { getAccessTokenSilently, user: currentUser } = useAppAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const dialogTitle = useMemo(
    () => (isEditing ? "Edit User" : "Add User"),
    [isEditing]
  );

  const loadUsers = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const users = await getUsers(token);
      setRows(users);
    } catch (loadError) {
      NotificationManager.error(loadError.message || "Unable to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateDialog = () => {
    setIsEditing(false);
    setSelectedUserId(null);
    setForm(emptyForm);
    setError("");
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setIsEditing(true);
    setSelectedUserId(user.id);
    setForm({
      name: user.name || "",
      email: user.email || "",
      role: user.role || "member",
      status: user.status || "active",
      password: "",
    });
    setError("");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (submitting) {
      return;
    }

    setDialogOpen(false);
  };

  const handleChange = (field) => (event) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: event.target.value,
    }));
  };

  const submitHandler = async () => {
    setSubmitting(true);
    setError("");

    try {
      const token = await getAccessTokenSilently();
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        status: form.status,
      };

      if (form.password) {
        payload.password = form.password;
      }

      if (isEditing) {
        await updateUser(token, selectedUserId, payload);
        NotificationManager.success("User updated successfully");
      } else {
        await createUser(token, payload);
        NotificationManager.success("User created successfully");
      }

      setDialogOpen(false);
      await loadUsers();
    } catch (submitError) {
      setError(submitError.message || "Unable to save user");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <Typography variant="h4">Users</Typography>
          <Typography variant="body1" color="textSecondary">
            Owners can manage access. Members can use the application.
          </Typography>
        </div>
        <Button color="primary" variant="contained" onClick={openCreateDialog}>
          Add User
        </Button>
      </div>

      <Paper className={styles.paper}>
        {loading ? (
          <Typography>Loading users...</Typography>
        ) : (
          <Table className={styles.table}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.role}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{formatDate(row.lastLoginAt)}</TableCell>
                  <TableCell align="right">
                    <Button
                      color="primary"
                      onClick={() => openEditDialog(row)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <div className={styles.form}>
            <TextField
              label="Name"
              value={form.name}
              onChange={handleChange("name")}
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={handleChange("email")}
            />
            <TextField
              select
              label="Role"
              value={form.role}
              onChange={handleChange("role")}
            >
              <MenuItem value="owner">Owner</MenuItem>
              <MenuItem value="member">Member</MenuItem>
            </TextField>
            <TextField
              select
              label="Status"
              value={form.status}
              onChange={handleChange("status")}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </TextField>
            <TextField
              className={styles.fullWidth}
              type="password"
              label={isEditing ? "New Password (optional)" : "Password"}
              value={form.password}
              onChange={handleChange("password")}
              helperText={isEditing ? "Leave blank to keep the current password" : "Minimum 8 characters"}
            />
          </div>
          <Typography variant="body2" className={styles.errorText}>
            {error}
          </Typography>
          {isEditing && currentUser?.id === selectedUserId && (
            <Typography variant="body2" color="textSecondary">
              Your own owner access and active status cannot be removed.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" onClick={submitHandler} disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Users;
